// Three.js set pieces the androids perform with, one per cinematic section.
// Each prop fits a unit radius around its origin; the stage scales it to its
// [data-prop] slot. Pure three.js so the splash reel can reuse them.
import * as THREE from "three";
import { RoundedBoxGeometry } from "three/examples/jsm/geometries/RoundedBoxGeometry.js";

const PROP_NAMES = ["halo", "orbit", "monoliths", "carousel", "globe", "network", "signal", "bars", "voices"] as const;
export type PropName = (typeof PROP_NAMES)[number];
export const isProp = (s: string | undefined): s is PropName => !!s && (PROP_NAMES as readonly string[]).includes(s);

export interface PropCtx {
  /** Scroll progress through the section, 0..1. */
  p: number;
  /** Active step for pinned sections. */
  step: number;
  pointer: { x: number; y: number };
}
export interface Prop {
  group: THREE.Group;
  update(t: number, dt: number, ctx: PropCtx): void;
  dispose(): void;
}

// Theme palette, read when a prop is created (Cast rebuilds props on a theme
// change). Dark: glowing additive light. Light: solid logo blue + navy ink,
// since additive light disappears on a pale page.
let ACCENT = "#6fa8e8";
let INK = "#ffffff";
let DOTS = "#cfd3da";
let BLEND: THREE.Blending = THREE.AdditiveBlending;
export function setPropTheme(theme: "light" | "dark") {
  const light = theme === "light";
  ACCENT = light ? "#2a629e" : "#6fa8e8";
  INK = light ? "#0c1b32" : "#ffffff";
  DOTS = light ? "#4d5e76" : "#cfd3da";
  BLEND = light ? THREE.NormalBlending : THREE.AdditiveBlending;
}
const rnd = (seed: number) => () => {
  seed |= 0; seed = (seed + 0x6d2b79f5) | 0;
  let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};
const damp = THREE.MathUtils.damp;

function kit() {
  const disposables: Array<{ dispose(): void }> = [];
  const keep = <T extends { dispose(): void }>(x: T) => (disposables.push(x), x);
  const glow = (color = ACCENT, opacity = 1) =>
    keep(new THREE.MeshBasicMaterial({ color, transparent: true, opacity, blending: BLEND, depthWrite: false, toneMapped: false }));
  const line = (color = ACCENT, opacity = 0.6) =>
    keep(new THREE.LineBasicMaterial({ color, transparent: true, opacity, blending: BLEND, depthWrite: false, toneMapped: false }));
  const chrome = () => keep(new THREE.MeshPhysicalMaterial({ color: "#0c1b32", metalness: 1, roughness: 0.18, clearcoat: 1, clearcoatRoughness: 0.05 }));
  return { keep, glow, line, chrome, dispose: () => disposables.forEach((d) => d.dispose()) };
}

const circlePoints = (r: number, n = 128) =>
  Array.from({ length: n + 1 }, (_, i) => {
    const a = (i / n) * Math.PI * 2;
    return new THREE.Vector3(Math.cos(a) * r, 0, Math.sin(a) * r);
  });

// ── halo: rings + light beams + floor glow behind the hero pair ────────────
function halo(): Prop {
  const k = kit();
  const group = new THREE.Group();
  const rings = new THREE.Group();
  group.add(rings);
  const r1 = new THREE.Mesh(k.keep(new THREE.TorusGeometry(1, 0.004, 8, 160)), k.glow(ACCENT, 0.9));
  const r2 = new THREE.Mesh(k.keep(new THREE.TorusGeometry(0.82, 0.002, 8, 160)), k.glow(INK, 0.35));
  const r3 = new THREE.Mesh(k.keep(new THREE.TorusGeometry(1.18, 0.0015, 8, 160)), k.glow(ACCENT, 0.3));
  rings.add(r1, r2, r3);
  const beamGeo = k.keep(new THREE.PlaneGeometry(0.035, 3.2));
  const beams: THREE.Mesh[] = [];
  for (let i = 0; i < 9; i++) {
    const b = new THREE.Mesh(beamGeo, k.glow(ACCENT, 0.08 + (i % 3) * 0.04));
    b.position.set((i - 4) * 0.26, 0.4, -0.4);
    group.add(b);
    beams.push(b);
  }
  // floor pool of light
  const c = document.createElement("canvas");
  c.width = c.height = 128;
  const g = c.getContext("2d")!;
  const grad = g.createRadialGradient(64, 64, 0, 64, 64, 64);
  grad.addColorStop(0, "rgba(74,138,201,0.6)");
  grad.addColorStop(1, "rgba(74,138,201,0)");
  g.fillStyle = grad;
  g.fillRect(0, 0, 128, 128);
  const tex = k.keep(new THREE.CanvasTexture(c));
  const floorMat = k.glow("#ffffff", 0.9);
  floorMat.map = tex;
  const floor = new THREE.Mesh(k.keep(new THREE.PlaneGeometry(2.6, 2.6)), floorMat);
  floor.rotation.x = -Math.PI / 2;
  floor.position.y = -1;
  group.add(floor);
  return {
    group,
    update(t, dt, { p, pointer }) {
      rings.rotation.x = damp(rings.rotation.x, 0.15 + pointer.y * 0.2, 3, dt);
      rings.rotation.y = damp(rings.rotation.y, pointer.x * 0.35 + p * 1.2, 3, dt);
      r2.rotation.x = t * 0.25;
      r3.rotation.y = -t * 0.18;
      beams.forEach((b, i) => {
        b.position.x = (i - 4) * 0.26 + Math.sin(t * 0.4 + i) * 0.05;
        (b.material as THREE.MeshBasicMaterial).opacity = 0.05 + 0.08 * (0.5 + 0.5 * Math.sin(t * 0.9 + i * 1.7));
      });
    },
    dispose: k.dispose,
  };
}

// ── orbit: three city nodes on a tilted ring, linked by arcs ───────────────
function orbit(): Prop {
  const k = kit();
  const group = new THREE.Group();
  const tilt = new THREE.Group();
  tilt.rotation.set(1.15, 0, 0.2);
  group.add(tilt);
  const ring = new THREE.LineLoop(k.keep(new THREE.BufferGeometry().setFromPoints(circlePoints(1))), k.line(ACCENT, 0.5));
  const ring2 = new THREE.LineLoop(k.keep(new THREE.BufferGeometry().setFromPoints(circlePoints(0.7))), k.line(INK, 0.18));
  tilt.add(ring, ring2);
  const nodeGeo = k.keep(new THREE.SphereGeometry(0.045, 20, 16));
  const nodes = [0, 1, 2].map(() => {
    const n = new THREE.Mesh(nodeGeo, k.glow(ACCENT, 1));
    const h = new THREE.Mesh(nodeGeo, k.glow(ACCENT, 0.25));
    h.scale.setScalar(2.4);
    n.add(h);
    tilt.add(n);
    return n;
  });
  const arcGeo = [0, 1, 2].map(() => k.keep(new THREE.BufferGeometry().setFromPoints(Array.from({ length: 33 }, () => new THREE.Vector3()))));
  arcGeo.forEach((g) => group.add(new THREE.Line(g, k.line(ACCENT, 0.35))));
  const tmp = [new THREE.Vector3(), new THREE.Vector3(), new THREE.Vector3()];
  return {
    group,
    update(t, dt, { p, pointer }) {
      group.rotation.y = damp(group.rotation.y, pointer.x * 0.4, 3, dt);
      nodes.forEach((n, i) => {
        const a = t * 0.22 + p * 2 + (i * Math.PI * 2) / 3;
        n.position.set(Math.cos(a), 0, Math.sin(a));
        n.getWorldPosition(tmp[i]);
        group.worldToLocal(tmp[i]);
      });
      arcGeo.forEach((g, i) => {
        const a = tmp[i], b = tmp[(i + 1) % 3];
        const pos = g.attributes.position as THREE.BufferAttribute;
        for (let j = 0; j <= 32; j++) {
          const f = j / 32;
          const v = a.clone().lerp(b, f);
          v.multiplyScalar(1 + Math.sin(f * Math.PI) * 0.35);
          pos.setXYZ(j, v.x, v.y, v.z);
        }
        pos.needsUpdate = true;
      });
    },
    dispose: k.dispose,
  };
}

// ── monoliths: four slabs, the active pillar rises and lights up ───────────
function monoliths(): Prop {
  const k = kit();
  const group = new THREE.Group();
  const slabGeo = k.keep(new RoundedBoxGeometry(0.3, 1.5, 0.06, 2, 0.015));
  const edgeGeo = k.keep(new THREE.EdgesGeometry(new THREE.BoxGeometry(0.3, 1.5, 0.06)));
  const stripGeo = k.keep(new THREE.PlaneGeometry(0.012, 1.3));
  const slabs = [0, 1, 2, 3].map((i) => {
    const g = new THREE.Group();
    g.position.set((i - 1.5) * 0.5, 0, -Math.abs(i - 1.5) * 0.25);
    g.rotation.y = (1.5 - i) * 0.25;
    g.add(new THREE.Mesh(slabGeo, k.chrome()));
    const edges = new THREE.LineSegments(edgeGeo, k.line(ACCENT, 0.5));
    g.add(edges);
    const strip = new THREE.Mesh(stripGeo, k.glow(ACCENT, 0.2));
    strip.position.z = 0.032;
    g.add(strip);
    group.add(g);
    return { g, edges, strip };
  });
  return {
    group,
    update(t, dt, { step, pointer }) {
      group.rotation.y = damp(group.rotation.y, pointer.x * 0.25, 3, dt);
      slabs.forEach(({ g, edges, strip }, i) => {
        const on = i === step ? 1 : 0;
        g.position.y = damp(g.position.y, on * 0.18 + Math.sin(t * 0.8 + i) * 0.02, 5, dt);
        const sm = strip.material as THREE.MeshBasicMaterial;
        sm.opacity = damp(sm.opacity, on ? 0.9 : 0.12, 6, dt);
        const em = edges.material as THREE.LineBasicMaterial;
        em.opacity = damp(em.opacity, on ? 0.95 : 0.25, 6, dt);
      });
    },
    dispose: k.dispose,
  };
}

// ── carousel: six product tiles on a ring, active product turns to camera ──
function carousel(): Prop {
  const k = kit();
  const group = new THREE.Group();
  const spin = new THREE.Group();
  group.add(spin);
  const tileGeo = k.keep(new RoundedBoxGeometry(0.46, 0.62, 0.025, 2, 0.02));
  const frameGeo = k.keep(new THREE.EdgesGeometry(new THREE.PlaneGeometry(0.46, 0.62)));
  const glyphs: THREE.BufferGeometry[] = [
    new THREE.TorusGeometry(0.09, 0.025, 12, 40), // voice
    new THREE.BoxGeometry(0.15, 0.15, 0.15), // desk
    new THREE.OctahedronGeometry(0.12), // HR
    new THREE.CylinderGeometry(0.1, 0.1, 0.05, 6), // books (coin)
    new THREE.ConeGeometry(0.1, 0.18, 4), // media
    new THREE.TorusKnotGeometry(0.07, 0.02, 64, 8), // logistics
  ].map((g) => k.keep(g));
  const items = glyphs.map((geo, i) => {
    const a = (i / 6) * Math.PI * 2;
    const g = new THREE.Group();
    g.position.set(Math.sin(a) * 1, 0, Math.cos(a) * 1);
    g.rotation.y = a;
    const glass = new THREE.MeshPhysicalMaterial({ color: "#0f2038", metalness: 0.6, roughness: 0.15, transparent: true, opacity: 0.7, clearcoat: 1 });
    k.keep(glass);
    g.add(new THREE.Mesh(tileGeo, glass));
    const frame = new THREE.LineSegments(frameGeo, k.line(ACCENT, 0.6));
    frame.position.z = 0.014;
    g.add(frame);
    const glyph = new THREE.Mesh(geo, k.keep(new THREE.MeshPhysicalMaterial({ color: "#4480ba", metalness: 1, roughness: 0.25, emissive: "#1b4a88" })));
    glyph.position.z = 0.12;
    g.add(glyph);
    spin.add(g);
    return { g, glyph, frame };
  });
  return {
    group,
    update(t, dt, { step, pointer }) {
      const target = -(step / 6) * Math.PI * 2;
      // shortest path to the target angle
      let d = target - spin.rotation.y;
      d = Math.atan2(Math.sin(d), Math.cos(d));
      spin.rotation.y = damp(spin.rotation.y, spin.rotation.y + d, 4, dt);
      group.rotation.x = damp(group.rotation.x, 0.12 + pointer.y * 0.15, 3, dt);
      items.forEach(({ glyph, frame }, i) => {
        glyph.rotation.y = t * 0.8 + i;
        glyph.rotation.x = t * 0.4;
        const fm = frame.material as THREE.LineBasicMaterial;
        fm.opacity = damp(fm.opacity, i === step ? 1 : 0.25, 6, dt);
        glyph.scale.setScalar(damp(glyph.scale.x, i === step ? 1.25 : 0.85, 6, dt));
      });
    },
    dispose: k.dispose,
  };
}

// ── globe: dotted sphere with blue meridians, turns with scroll ────────────
function globe(): Prop {
  const k = kit();
  const group = new THREE.Group();
  const spin = new THREE.Group();
  spin.rotation.z = 0.35;
  group.add(spin);
  const n = 1800;
  const pts = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) {
    const y = 1 - (i / (n - 1)) * 2;
    const r = Math.sqrt(1 - y * y);
    const a = i * 2.399963;
    pts.set([Math.cos(a) * r, y, Math.sin(a) * r], i * 3);
  }
  const geo = k.keep(new THREE.BufferGeometry());
  geo.setAttribute("position", new THREE.BufferAttribute(pts, 3));
  const dots = new THREE.Points(geo, k.keep(new THREE.PointsMaterial({ color: DOTS, size: 0.018, transparent: true, opacity: 0.75, depthWrite: false })));
  spin.add(dots);
  const merid = new THREE.Group();
  spin.add(merid);
  const loop = k.keep(new THREE.BufferGeometry().setFromPoints(circlePoints(1.01)));
  [0, 0.6, 1.2, -0.6].forEach((r, i) => {
    const l = new THREE.LineLoop(loop, k.line(ACCENT, i === 0 ? 0.7 : 0.35));
    l.rotation.set(Math.PI / 2, r, 0);
    merid.add(l);
  });
  const equator = new THREE.LineLoop(k.keep(new THREE.BufferGeometry().setFromPoints(circlePoints(1.25))), k.line(ACCENT, 0.25));
  group.add(equator);
  return {
    group,
    update(t, dt, { p, pointer }) {
      spin.rotation.y = t * 0.12 + p * Math.PI * 1.5;
      group.rotation.x = damp(group.rotation.x, pointer.y * 0.25, 3, dt);
      equator.rotation.x = 1.3 + Math.sin(t * 0.3) * 0.05;
    },
    dispose: k.dispose,
  };
}

// ── network: a constellation of 70 nodes (the Hub's practitioners) ─────────
function network(): Prop {
  const k = kit();
  const group = new THREE.Group();
  const r = rnd(70);
  const nodes = Array.from({ length: 70 }, () => new THREE.Vector3((r() - 0.5) * 2.4, (r() - 0.5) * 1.5, (r() - 0.5) * 1.2));
  const inst = new THREE.InstancedMesh(k.keep(new THREE.SphereGeometry(0.022, 10, 8)), k.glow(ACCENT, 1), nodes.length);
  const m = new THREE.Matrix4();
  nodes.forEach((v, i) => inst.setMatrixAt(i, m.makeTranslation(v.x, v.y, v.z)));
  group.add(inst);
  const edges: Array<[number, number]> = [];
  const seg: number[] = [];
  nodes.forEach((a, i) =>
    nodes.forEach((b, j) => {
      if (j > i && a.distanceTo(b) < 0.42) {
        edges.push([i, j]);
        seg.push(a.x, a.y, a.z, b.x, b.y, b.z);
      }
    }),
  );
  const lg = k.keep(new THREE.BufferGeometry());
  lg.setAttribute("position", new THREE.Float32BufferAttribute(seg, 3));
  group.add(new THREE.LineSegments(lg, k.line(INK, 0.16)));
  const pulses = 18;
  const pg = k.keep(new THREE.BufferGeometry());
  const pp = new Float32Array(pulses * 3);
  pg.setAttribute("position", new THREE.BufferAttribute(pp, 3));
  group.add(new THREE.Points(pg, k.keep(new THREE.PointsMaterial({ color: ACCENT, size: 0.05, transparent: true, blending: BLEND, depthWrite: false, toneMapped: false }))));
  return {
    group,
    update(t, dt, { p, pointer }) {
      group.rotation.y = damp(group.rotation.y, pointer.x * 0.5 + p * 0.8 + t * 0.03, 3, dt);
      group.rotation.x = damp(group.rotation.x, pointer.y * 0.2, 3, dt);
      if (!edges.length) return;
      for (let i = 0; i < pulses; i++) {
        const [a, b] = edges[(i * 7) % edges.length];
        const f = (t * 0.6 + i * 0.37) % 1;
        const va = nodes[a], vb = nodes[b];
        pp.set([va.x + (vb.x - va.x) * f, va.y + (vb.y - va.y) * f, va.z + (vb.z - va.z) * f], i * 3);
      }
      (pg.attributes.position as THREE.BufferAttribute).needsUpdate = true;
    },
    dispose: k.dispose,
  };
}

// ── signal: an outgoing call — rings expanding from a glowing core ─────────
function signal(): Prop {
  const k = kit();
  const group = new THREE.Group();
  const core = new THREE.Mesh(k.keep(new THREE.SphereGeometry(0.08, 24, 18)), k.glow(ACCENT, 1));
  const coreHalo = new THREE.Mesh(core.geometry, k.glow(ACCENT, 0.2));
  coreHalo.scale.setScalar(2.5);
  group.add(core, coreHalo);
  const ringGeo = k.keep(new THREE.TorusGeometry(1, 0.006, 8, 120));
  const rings = Array.from({ length: 5 }, () => {
    const m = new THREE.Mesh(ringGeo, k.glow(ACCENT, 0.6));
    group.add(m);
    return m;
  });
  return {
    group,
    update(t, dt, { pointer }) {
      group.rotation.y = damp(group.rotation.y, pointer.x * 0.6, 3, dt);
      group.rotation.x = damp(group.rotation.x, -pointer.y * 0.4, 3, dt);
      rings.forEach((m, i) => {
        const f = (t * 0.35 + i / rings.length) % 1;
        m.scale.setScalar(0.15 + f * 1.05);
        (m.material as THREE.MeshBasicMaterial).opacity = (1 - f) * 0.7;
      });
      coreHalo.scale.setScalar(2.2 + Math.sin(t * 3) * 0.3);
    },
    dispose: k.dispose,
  };
}

// ── bars: four data columns that grow as the stats act scrolls in ─────────
function bars(): Prop {
  const k = kit();
  const group = new THREE.Group();
  const HEIGHTS = [1, 0.86, 0.58, 0.36]; // members, trainings, clients, countries (log-scaled)
  const colGeo = k.keep(new RoundedBoxGeometry(0.26, 1, 0.26, 2, 0.03));
  const capGeo = k.keep(new THREE.BoxGeometry(0.27, 0.012, 0.27));
  const edgeGeo = k.keep(new THREE.EdgesGeometry(new THREE.BoxGeometry(0.26, 1, 0.26)));
  const cols = HEIGHTS.map((h, i) => {
    const g = new THREE.Group();
    g.position.set((i - 1.5) * 0.5, -1, -Math.abs(i - 1.5) * 0.12);
    const body = new THREE.Mesh(colGeo, k.chrome());
    const edges = new THREE.LineSegments(edgeGeo, k.line(ACCENT, 0.45));
    const cap = new THREE.Mesh(capGeo, k.glow(ACCENT, 0.95));
    g.add(body, edges, cap);
    group.add(g);
    return { g, body, edges, cap, h, cur: 0.02 };
  });
  // rising motes
  const n = 90;
  const r = rnd(11);
  const mp = new Float32Array(n * 3);
  const seeds = Array.from({ length: n }, () => [(r() - 0.5) * 2, r(), (r() - 0.5) * 0.6]);
  const mg = k.keep(new THREE.BufferGeometry());
  mg.setAttribute("position", new THREE.BufferAttribute(mp, 3));
  group.add(new THREE.Points(mg, k.keep(new THREE.PointsMaterial({ color: ACCENT, size: 0.025, transparent: true, opacity: 0.8, blending: BLEND, depthWrite: false, toneMapped: false }))));
  return {
    group,
    update(t, dt, { p, pointer }) {
      group.rotation.y = damp(group.rotation.y, -0.25 + pointer.x * 0.3, 3, dt);
      group.rotation.x = damp(group.rotation.x, 0.12 + pointer.y * 0.1, 3, dt);
      const grow = THREE.MathUtils.smoothstep(p, 0.05, 0.5);
      cols.forEach((c, i) => {
        const target = Math.max(0.02, c.h * 1.8 * THREE.MathUtils.smoothstep(grow, i * 0.12, i * 0.12 + 0.6));
        c.cur = damp(c.cur, target, 5, dt);
        c.body.scale.y = c.cur;
        c.edges.scale.y = c.cur;
        c.body.position.y = c.cur / 2;
        c.edges.position.y = c.cur / 2;
        c.cap.position.y = c.cur + 0.01;
        (c.cap.material as THREE.MeshBasicMaterial).opacity = 0.6 + 0.4 * Math.sin(t * 2 + i);
      });
      seeds.forEach(([x, s, z], i) => mp.set([x, -1 + ((s + t * 0.12) % 1) * 2.2, z], i * 3));
      (mg.attributes.position as THREE.BufferAttribute).needsUpdate = true;
    },
    dispose: k.dispose,
  };
}

// ── voices: a radial equalizer that spikes on every new testimonial ──────
function voices(): Prop {
  const k = kit();
  const group = new THREE.Group();
  const n = 64;
  const barGeo = k.keep(new THREE.BoxGeometry(0.018, 1, 0.018));
  barGeo.translate(0, 0.5, 0);
  const inst = new THREE.InstancedMesh(barGeo, k.glow(ACCENT, 0.9), n);
  group.add(inst);
  const ring = new THREE.LineLoop(k.keep(new THREE.BufferGeometry().setFromPoints(circlePoints(0.62).map((v) => new THREE.Vector3(v.x, v.z, 0)))), k.line(INK, 0.25));
  group.add(ring);
  const core = new THREE.Mesh(k.keep(new THREE.TorusGeometry(0.28, 0.01, 8, 80)), k.glow(ACCENT, 0.8));
  group.add(core);
  const m = new THREE.Matrix4();
  const q = new THREE.Quaternion();
  const sc = new THREE.Vector3();
  const pos = new THREE.Vector3();
  let lastStep = -1;
  let kick = 0;
  return {
    group,
    update(t, dt, { step, pointer }) {
      if (step !== lastStep) {
        lastStep = step;
        kick = 1;
      }
      kick = damp(kick, 0, 2.2, dt);
      group.rotation.y = damp(group.rotation.y, pointer.x * 0.5, 3, dt);
      group.rotation.x = damp(group.rotation.x, -pointer.y * 0.3, 3, dt);
      group.rotation.z = t * 0.08;
      for (let i = 0; i < n; i++) {
        const a = (i / n) * Math.PI * 2;
        const h = 0.08 + 0.18 * (0.5 + 0.5 * Math.sin(t * 3 + i * 0.7)) * (0.5 + 0.5 * Math.sin(t * 1.3 + i * 0.23)) + kick * 0.35 * (0.5 + 0.5 * Math.sin(i * 1.9));
        pos.set(Math.cos(a) * 0.66, Math.sin(a) * 0.66, 0);
        q.setFromAxisAngle(new THREE.Vector3(0, 0, 1), a - Math.PI / 2);
        sc.set(1, h, 1);
        inst.setMatrixAt(i, m.compose(pos, q, sc));
      }
      inst.instanceMatrix.needsUpdate = true;
      core.scale.setScalar(1 + kick * 0.25 + Math.sin(t * 2.5) * 0.03);
    },
    dispose: k.dispose,
  };
}

const FACTORY: Record<PropName, () => Prop> = { halo, orbit, monoliths, carousel, globe, network, signal, bars, voices };
export const createProp = (name: PropName) => FACTORY[name]();
